import { Button } from '@givebutter/primitives/button';
import { unmaskNumber } from '@givebutter/util';
import { faEdit, faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as EditPaidInstallmentDialog from 'assets/js/pages/pledges/EditPaidInstallmentDialog';
import { useInstallmentCalculation } from 'assets/js/pages/pledges/useInstallmentCalculation';
import { InstallmentStatus, PledgeFormValue } from 'assets/js/pages/pledges/usePledgeForm';
import { useGivebutterFormContext } from 'components/form';
import * as Form from 'components/form';
import { InputCurrencyIcon } from 'components/input';
import * as Tooltip from 'components/tooltip';
import { PledgeStatus } from 'constants/pledges';
import { DateTime } from 'luxon';
import { twMerge } from 'tailwind-merge';

interface EditInstallmentItemProps {
    index: number;
    canRemove?: boolean;
}

const EditInstallmentItem = ({ index, canRemove }: EditInstallmentItemProps) => {
    const methods = useGivebutterFormContext<PledgeFormValue>();
    const { watch, control, setValue, clearErrors } = methods;
    const { appendInstallment, removeInstallment } = useInstallmentCalculation(methods);
    const [total, pledgeStatus, status, date] = watch([
        'amount',
        'status_friendly',
        `installments.${index}.status`,
        `installments.${index}.date`,
    ]);
    const paid = status === InstallmentStatus.Paid;
    const missed =
        status === InstallmentStatus.Late &&
        date < DateTime.now().endOf('day') &&
        pledgeStatus !== PledgeStatus.Fulfilled;

    return (
        <div className="flex gap-3">
            <div className="flex gap-3">
                <Form.Container>
                    <Form.Label name={`installment.${index}.date`}>
                        Installment {index + 1}{' '}
                        {paid && (
                            <span className="inline-block rounded-full bg-green-50 px-2 text-xs text-green-700">
                                Paid
                            </span>
                        )}
                        {missed && (
                            <span className="inline-block rounded-full bg-red-50 px-2 text-xs text-red-700">
                                Late
                            </span>
                        )}
                    </Form.Label>
                    <Form.UseFormDatePicker
                        control={control}
                        className={twMerge('w-full', paid && 'cursor-not-allowed opacity-30')}
                        name={`installments.${index}.date`}
                        disabled={paid}
                        startInputProps={{
                            disabled: paid,
                        }}
                        onChange={() => clearErrors('installments')}
                    />
                </Form.Container>
                <Form.Container>
                    <Form.Label name={`installment.${index}.amount`}>Amount</Form.Label>
                    <Form.TextInputRoot
                        control={control}
                        name={`installments.${index}.amount`}
                        rules={{
                            validate: {
                                max: value =>
                                    unmaskNumber(value) <= unmaskNumber(total) ||
                                    'Amount must be less than total pledge.',
                            },
                        }}
                        inputRootProps={{
                            mask: 'decimal',
                            className: paid ? 'cursor-not-allowed opacity-30' : '',
                        }}
                    >
                        <InputCurrencyIcon />
                        <Form.TextInputForm
                            onKeyUp={() => setValue(`installments.${index}.override`, true)}
                            readOnly={paid}
                        />
                    </Form.TextInputRoot>
                    <Form.Error name={`installments.${index}.amount`} />
                </Form.Container>
            </div>
            <div className="mt-5 flex min-w-16">
                {paid ? (
                    <Tooltip.Root>
                        <Tooltip.Trigger asChild>
                            <EditPaidInstallmentDialog.Root index={index}>
                                <EditPaidInstallmentDialog.Trigger asChild>
                                    <Button
                                        variant="transparent"
                                        shape={'square'}
                                        aria-label="Edit paid installment"
                                    >
                                        <FontAwesomeIcon icon={faEdit} />
                                    </Button>
                                </EditPaidInstallmentDialog.Trigger>
                            </EditPaidInstallmentDialog.Root>
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content inverse>
                                <Tooltip.Arrow inverse />
                                <span className="text-white">Edit paid installment</span>
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>
                ) : (
                    <>
                        {canRemove && (
                            <RemoveInstallment onRemove={() => removeInstallment(index)} />
                        )}
                    </>
                )}
                <AddInstallment onAppend={appendInstallment} />
            </div>
        </div>
    );
};

const AddInstallment = ({ onAppend }: { onAppend: () => void }) => {
    return (
        <Tooltip.Root>
            <Tooltip.Trigger asChild>
                <Button
                    variant="transparent"
                    shape={'square'}
                    onClick={onAppend}
                    aria-label="Add installment"
                >
                    <FontAwesomeIcon icon={faPlus} />
                </Button>
            </Tooltip.Trigger>
            <Tooltip.Portal>
                <Tooltip.Content inverse>
                    <Tooltip.Arrow inverse />
                    <span className="text-white">Add installment</span>
                </Tooltip.Content>
            </Tooltip.Portal>
        </Tooltip.Root>
    );
};

const RemoveInstallment = ({ onRemove }: { onRemove: () => void }) => {
    return (
        <Tooltip.Root>
            <Tooltip.Trigger asChild>
                <Button
                    variant="transparent"
                    shape={'square'}
                    onClick={onRemove}
                    aria-label="Remove installment"
                >
                    <FontAwesomeIcon icon={faTrash} />
                </Button>
            </Tooltip.Trigger>
            <Tooltip.Portal>
                <Tooltip.Content inverse>
                    <Tooltip.Arrow inverse />
                    <span className="text-white">Remove installment</span>
                </Tooltip.Content>
            </Tooltip.Portal>
        </Tooltip.Root>
    );
};

export { EditInstallmentItem };
